import React from 'react';
import { EMPTY_RECORD, useDetailRecord } from 'helpers/detailRequest';
import { useDispatch } from "react-redux";
import { Suspense, lazy, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getWaiver, sendToApproveWaiver } from "redux/crm/project/waiverSlice";
import WaiverDetailHeader from "./detail/WaiverDetailHeader";
import { Button, Card, Tabs, Alert, DetailRecordFallback } from 'components/common';
import { MdHistory, MdSendAndArchive, MdVerified } from "react-icons/md";
import { FaBan, FaCheck, FaCheckSquare, FaClosedCaptioning, FaRegListAlt } from "react-icons/fa";
import WaiverDetailForm from "./detail/WaiverDetailForm";
import { Link } from "react-router-dom";
import WaiverActivity from "./detail/WaiverActivity";

const LazyWaiverApproveForm = lazy(() => import("./detail/WaiverApproveForm"));
const LazyWaiverRejectForm = lazy(() => import("./detail/WaiverRejectForm"));
const LazyWaiverCSDAcceptForm = lazy(() => import("./detail/WaiverCSDAcceptForm"));
const LazyWaiverCSDReVerifyForm = lazy(() => import("./detail/WaiverCSDReverifyForm"));
const LazyWaiverReSubmitToCSDForm = lazy(() => import("./detail/WaiverReSubmitToCSDForm"));
const LazyWaiverGMApproveForm = lazy(() => import("./detail/WaiverGMApproveForm"));
const LazyWaiverClearWaiverForm = lazy(() => import("./detail/WaiverClearWaiverForm"));
const LazyWaiverCreditControllerVerifyForm = lazy(() => import("./detail/WaiverCreditControllerVerifyForm"));

const WaiverDetail = () => {
  const dispatch = useDispatch();
  const params = useParams();

  const { project_id: projectId, id: waiverId } = params;
  // Only the record in the URL — the persisted store can still hold the one
  // viewed last (e.g. when opened from an email link).
  const { record, waiting, error } = useDetailRecord('crm_waiver', waiverId);
  const waiver = record || EMPTY_RECORD;
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showCSDAcceptForm, setShowCSDAcceptForm] = useState(false);
  const [showCSDVerifyForm, setShowCSDVerifyForm] = useState(false);
  const [showResubmitToCSDForm, setShowResubmitToCSDForm] = useState(false);
  const [showGMApproveForm, setShowGMApproveForm] = useState(false);
  const [showClearWaiverForm, setShowClearWaiverForm] = useState(false);
  const [showCreditControllerVerifyForm, setShowCreditControllerVerifyForm] = useState(false);

  const {
    allow_sales_manager_approve,
    allow_clear,
    allow_csd_leader_verify,
    allow_resubmit_to_csd,
    allow_general_manager_operation_approve,
    allow_credit_controller_verify,
    allow_finance_controller_approve
  } = waiver?.permissions || {};

  useEffect(() => {
    dispatch(getWaiver({ projectId, waiverId }))
  }, [dispatch, params, projectId, waiverId])

  const handleRequestApproval = () => {
    dispatch(sendToApproveWaiver({ projectId, waiverId }))
  }

  const handleApprove = () => {
    setShowApproveForm(true)
  }

  const handleReject = () => {
    setShowRejectForm(true)
  }

  const handleCSDAccept = () => {
    setShowCSDAcceptForm(true)
  }

  const handleCSDVerify = () => {
    setShowCSDVerifyForm(true)
  }

  const handleResubmitToCSD = () => {
    setShowResubmitToCSDForm(true)
  }

  const handleGMApprove = () => {
    setShowGMApproveForm(true)
  }

  const handleGMReject = () => {
    setShowRejectForm(true)
  }

  const handleClearWaiver = () => {
    setShowClearWaiverForm(true)
  }

  const handleCreditControllerVerify = () => {
    setShowCreditControllerVerifyForm(true)
  }

  const closed = waiver?.close?.closed || false;

  const renderMessage = () => {
    if (waiver.action_message) {
      return (
        <>
          <span dangerouslySetInnerHTML={{ __html: waiver.action_message }}></span>
          {(allow_sales_manager_approve || allow_finance_controller_approve) && (
            <>
              <Button size="sm" variant="primary" className="tw-ml-2 tw-mr-1" onClick={handleApprove}>
                <FaCheck /> Approve
              </Button>
              <Button size="sm" variant="danger" onClick={handleReject}>
                <FaBan /> Reject
              </Button>
            </>
          )}

          {allow_csd_leader_verify && (
            <>
              <Button size="sm" variant="primary" className="tw-ml-2 tw-mr-1" onClick={handleCSDAccept}>
                <FaCheck /> Accept
              </Button>
              <Button size="sm" variant="warning" onClick={handleCSDVerify}>
                <MdVerified /> Re-verify
              </Button>
            </>
          )}

          {allow_resubmit_to_csd && (
            <>
              <Button size="sm" variant="primary" className="tw-mx-2" onClick={handleResubmitToCSD}>
                <MdSendAndArchive /> Resubmit to CSD
              </Button>
            </>
          )}

          {allow_general_manager_operation_approve && (
            <>
              <Button size="sm" variant="primary" className="tw-ml-2 tw-mr-1" onClick={handleGMApprove}>
                <FaCheckSquare /> Approve
              </Button>
              <Button size="sm" variant="danger" onClick={handleGMReject}>
                <FaBan /> Reject
              </Button>
            </>
          )}

          {allow_credit_controller_verify && (
            <Button size="sm" variant="warning" className="tw-mx-2" onClick={handleCreditControllerVerify}>
              <MdVerified /> Verify
            </Button>
          )}

          {allow_clear && (
            <Button size="sm" variant="warning" className="tw-mx-2" onClick={handleClearWaiver}>
              <FaClosedCaptioning /> Clear waiver now!
            </Button>
          )}
        </>
      )
    } else {
      return (
        <span className="tw-text-sm">
          Waiver has not been sent for approval.
          <Link
            onClick={handleRequestApproval}
            className="tw-ml-2 tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-md tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 hover:tw-text-white tw-no-underline"
          > Send approval request now!</Link>
        </span>
      )
    }
  }

  if (!record) return <DetailRecordFallback waiting={waiting} title="Waiver not available" error={error} onRetry={() => dispatch(getWaiver({ projectId, waiverId }))} />;

  return (
    <>
      <WaiverDetailHeader waiver={waiver} />

      {!closed && (
        <Alert type="warning" className="tw-mb-3">
          {waiver?.has_overdue ? (
            <>
              <span>Commitment was overdue date!</span>
              {allow_clear && (
                <Button size="sm" variant="warning" className="tw-mx-2" onClick={handleClearWaiver}>
                  <FaClosedCaptioning /> Clear waiver now!
                </Button>
              )}
            </>
          ) : renderMessage()}
        </Alert>
      )}

      <Card padding="none">
        <Tabs
          variant="underline"
          lazyMount
          navClassName="tw-px-3 tw-pt-2 tw-bg-white"
          contentClassName="tw-bg-slate-50 tw-p-4"
          tabs={[
            {
              label: 'Detail',
              icon: <FaRegListAlt size={14} />,
              content: <WaiverDetailForm waiver={waiver} />,
            },
            {
              label: 'Activities',
              icon: <MdHistory size={14} />,
              content: <WaiverActivity />,
            },
          ]}
        />
      </Card>

      <Suspense fallback={<div>Loading...</div>}>
        {showApproveForm && (
          <LazyWaiverApproveForm
            showModal={showApproveForm}
            setshowModal={setShowApproveForm}
            waiver={waiver}
          />
        )}

        {showRejectForm && (
          <LazyWaiverRejectForm
            showModal={showRejectForm}
            setshowModal={setShowRejectForm}
            waiver={waiver}
          />
        )}

        {showCSDAcceptForm && (
          <LazyWaiverCSDAcceptForm
            showModal={showCSDAcceptForm}
            setshowModal={setShowCSDAcceptForm}
            waiver={waiver}
          />
        )}

        {showCSDVerifyForm && (
          <LazyWaiverCSDReVerifyForm
            showModal={showCSDVerifyForm}
            setshowModal={setShowCSDVerifyForm}
            waiver={waiver}
          />
        )}

        {showResubmitToCSDForm && (
          <LazyWaiverReSubmitToCSDForm
            showModal={showResubmitToCSDForm}
            setshowModal={setShowResubmitToCSDForm}
            waiver={waiver}
          />
        )}

        {showGMApproveForm && (
          <LazyWaiverGMApproveForm
            showModal={showGMApproveForm}
            setshowModal={setShowGMApproveForm}
            waiver={waiver}
          />
        )}

        {showClearWaiverForm && (
          <LazyWaiverClearWaiverForm
            showModal={showClearWaiverForm}
            setshowModal={setShowClearWaiverForm}
            waiver={waiver}
          />
        )}

        {showCreditControllerVerifyForm && (
          <LazyWaiverCreditControllerVerifyForm
            showModal={showCreditControllerVerifyForm}
            setshowModal={setShowCreditControllerVerifyForm}
            waiver={waiver}
          />
        )}
      </Suspense>
    </>
  )
}

export default WaiverDetail
